/**
 * Regime Lab tab — options-refined nowcast regime identification.
 * Microstates (Quiet/Neutral/Hot), regime transitions, and the walk-forward
 * conviction model (baseline vs options-augmented).
 */
import React from "react";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { AlphaVal, PanelSummary, RunBar, SigBadge, StatCard } from "./common";

const Th = ({ children, right }) => (
  <th className={`py-1.5 px-2 font-medium text-muted-foreground ${right ? "text-right" : "text-left"}`}>
    {children}
  </th>
);
const Td = ({ children, right }) => (
  <td className={`py-1.5 px-2 ${right ? "text-right" : ""}`}>{children}</td>
);

const QualityBadge = ({ quality }) => {
  const cls = quality === "Top Tier" ? "bg-emerald-600/20 text-emerald-500"
    : quality === "Strong" ? "bg-green-600/20 text-green-500"
    : quality === "Weak" ? "bg-red-600/20 text-red-400"
    : "bg-muted text-muted-foreground";
  return <Badge variant="outline" className={`text-[10px] ml-1 border-0 ${cls}`}>{quality}</Badge>;
};

/* ---------------- Microstates ---------------- */

const Microstates = ({ micro }) => {
  if (!micro?.per_regime) return null;
  const agg = micro.aggregate || {};
  return (
    <Card data-testid="regime-microstates">
      <CardHeader>
        <CardTitle className="text-base">Regime Microstates — Quiet / Neutral / Hot</CardTitle>
        <CardDescription>
          {micro.definition} Forward 63d sector-relative alpha per regime × options state.
          "Quiet" = options tape has NOT priced the move; "Hot" = fast money already bullish.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[["strong_regimes", "Strong regimes"], ["weak_regimes", "Weak regimes"], ["all", "All regimes"]].map(([k, label]) => {
            const a = agg[k];
            return (
              <StatCard key={k} label={`${label}: Quiet − Hot`} testId={`regime-agg-${k}`}>
                {a ? (
                  <span className="text-sm">
                    <AlphaVal value={a.quiet_minus_hot_pct} />{" "}
                    <span className="text-muted-foreground font-mono text-xs">(Welch t={a.welch_t})</span>{" "}
                    <SigBadge t={a.welch_t} />
                  </span>
                ) : <span className="text-muted-foreground text-sm">n/a</span>}
              </StatCard>
            );
          })}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <Th>Regime</Th>
                <Th right>Quiet</Th>
                <Th right>Neutral</Th>
                <Th right>Hot</Th>
                <Th right>Quiet − Hot</Th>
                <Th right>Sig</Th>
              </tr>
            </thead>
            <tbody>
              {micro.per_regime.map((r) => (
                <tr key={r.cluster} className="border-b border-border/50"
                    data-testid={`regime-micro-${r.cluster}`}>
                  <Td>
                    <span className="font-medium">{r.name}</span>
                    <QualityBadge quality={r.quality} />
                  </Td>
                  {["quiet", "neutral", "hot"].map((st) => (
                    <Td key={st} right>
                      {r.states?.[st] ? (
                        <>
                          <AlphaVal value={r.states[st].mean_pct} />{" "}
                          <span className="text-muted-foreground text-[10px]">n={r.states[st].n}</span>
                        </>
                      ) : "-"}
                    </Td>
                  ))}
                  <Td right>
                    {r.quiet_minus_hot_pct != null ? (
                      <><AlphaVal value={r.quiet_minus_hot_pct} />{" "}
                      <span className="text-muted-foreground font-mono text-xs">(t={r.welch_t})</span></>
                    ) : "-"}
                  </Td>
                  <Td right><SigBadge t={r.welch_t} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ---------------- Transitions ---------------- */

const Transitions = ({ trans }) => {
  if (!trans?.per_regime) return null;
  const destStr = (dests) =>
    (dests || []).map((d) => `${d.name} ${d.share_pct}%`).join(" · ") || "-";
  return (
    <Card data-testid="regime-transitions">
      <CardHeader>
        <CardTitle className="text-base">Regime Transitions — does a hot tape predict degradation?</CardTitle>
        <CardDescription>
          Where is each name's regime {trans.horizon} later? {trans.note}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <Th>Regime</Th>
                <Th right>P(stay)</Th>
                <Th right>Stay: Quiet</Th>
                <Th right>Stay: Hot</Th>
                <Th right>→Weak: Quiet</Th>
                <Th right>→Weak: Hot</Th>
                <Th right>z (→weak)</Th>
              </tr>
            </thead>
            <tbody>
              {trans.per_regime.map((r) => (
                <tr key={r.cluster} className="border-b border-border/50"
                    data-testid={`regime-trans-${r.cluster}`}>
                  <Td>
                    <span className="font-medium">{r.name}</span>
                    <QualityBadge quality={r.quality} />
                    <div className="text-[10px] text-muted-foreground">
                      Hot leavers → {destStr(r.top_destinations_hot)}
                    </div>
                  </Td>
                  <Td right><span className="font-mono text-xs">{r.p_stay_pct ?? "-"}%</span></Td>
                  <Td right><span className="font-mono text-xs">{r.p_stay_quiet_pct ?? "-"}%</span></Td>
                  <Td right><span className="font-mono text-xs">{r.p_stay_hot_pct ?? "-"}%</span></Td>
                  <Td right><span className="font-mono text-xs">{r.p_to_weak_quiet_pct ?? "-"}{r.p_to_weak_quiet_pct != null ? "%" : ""}</span></Td>
                  <Td right><span className="font-mono text-xs">{r.p_to_weak_hot_pct ?? "-"}{r.p_to_weak_hot_pct != null ? "%" : ""}</span></Td>
                  <Td right>
                    <span className="font-mono text-xs">{r.z_to_weak_hot_vs_quiet ?? "-"}</span>{" "}
                    {r.z_to_weak_hot_vs_quiet != null && <SigBadge t={r.z_to_weak_hot_vs_quiet} />}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ---------------- Conviction model ---------------- */

const Conviction = ({ conv }) => {
  if (!conv) return null;
  const auc = conv.auc || {};
  const port = conv.strong_regime_conviction_portfolio || {};
  return (
    <Card data-testid="regime-conviction">
      <CardHeader>
        <CardTitle className="text-base">Conviction Model — do we believe the regime label?</CardTitle>
        <CardDescription>
          Walk-forward classifier predicting regime-consistent behavior ({conv.target}).
          {" "}{conv.protocol}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[["baseline", "Baseline AUC (clusters + flow)"], ["augmented", "+ Options AUC"]].map(([k, label]) => {
            const a = auc[k];
            return (
              <StatCard key={k} label={label} testId={`regime-auc-${k}`}>
                {a ? (
                  <span className="text-sm font-mono">
                    {a.mean_auc}{" "}
                    <span className="text-muted-foreground text-xs">(t vs 0.5 = {a.nw_tstat_vs_half})</span>
                  </span>
                ) : <span className="text-muted-foreground text-sm">n/a</span>}
              </StatCard>
            );
          })}
          <StatCard label="Options uplift (paired ΔAUC)" testId="regime-auc-delta">
            {auc.paired_delta ? (
              <span className="text-sm">
                <span className="font-mono">
                  {auc.paired_delta.mean_delta_auc > 0 ? "+" : ""}{auc.paired_delta.mean_delta_auc}
                </span>{" "}
                <span className="text-muted-foreground font-mono text-xs">
                  (t={auc.paired_delta.nw_tstat}, {auc.paired_delta.pct_positive}% dates +)
                </span>{" "}
                <SigBadge t={auc.paired_delta.nw_tstat} />
              </span>
            ) : <span className="text-muted-foreground text-sm">n/a</span>}
          </StatCard>
        </div>
        <div className="border rounded-lg p-3 space-y-2" data-testid="regime-conviction-portfolio">
          <div className="text-sm font-medium">Strong-regime conviction portfolio (top vs bottom conviction tercile)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[["baseline", "Baseline conviction"], ["augmented", "Options-augmented conviction"]].map(([k, label]) => {
              const p = port[k];
              return (
                <div key={k} className="bg-muted/40 rounded p-2 text-xs">
                  <div className="text-muted-foreground mb-1">{label}</div>
                  {p ? (
                    <div>
                      <AlphaVal value={p.top_minus_bottom_pct} />{" "}
                      <span className="text-muted-foreground font-mono">
                        /3M (t={p.nw_tstat}, {p.pct_positive}% dates +)
                      </span>{" "}
                      <SigBadge t={p.nw_tstat} />
                    </div>
                  ) : <div className="text-muted-foreground">n/a</div>}
                </div>
              );
            })}
          </div>
          <div className="text-[11px] text-muted-foreground">{port.note}</div>
        </div>
        <div className="text-xs text-muted-foreground">
          Evaluated on {conv.n_eval_obs?.toLocaleString()} options-covered OOS observations
          across {conv.n_eval_dates} dates. {conv.note}
        </div>
      </CardContent>
    </Card>
  );
};

/* ---------------- Tab ---------------- */

export const RegimeLabTab = ({ signal }) => {
  const { result, job, running, run } = signal;
  const res = result?.result;

  return (
    <div className="space-y-4" data-testid="regimelab-tab">
      <Card>
        <CardHeader>
          <CardTitle>Regime Lab — options-refined regime identification</CardTitle>
          <CardDescription>
            The nowcast regime says <em>what kind of trade</em> this is; the options tape says
            <em> whether the move is already priced</em>. This lab validates regime microstates
            (Quiet/Hot), options-conditioned transitions, and a walk-forward conviction score —
            before promoting refined regimes into production nowcasting output.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RunBar result={result} job={job} running={running} onRun={run} testId="regimelab-runbar" />
          {res && (
            <>
              <PanelSummary panel={res.panel} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Options-covered obs">
                  {res.coverage?.options_covered_obs?.toLocaleString()}
                </StatCard>
                <StatCard label="Covered dates">{res.coverage?.covered_dates}</StatCard>
                <StatCard label="Avg names/date">
                  {res.coverage?.avg_covered_names_per_date}
                </StatCard>
                <StatCard label="Coverage window">
                  <span className="text-sm">
                    {res.coverage?.covered_window?.start} → {res.coverage?.covered_window?.end}
                  </span>
                </StatCard>
              </div>
              <div className="text-xs text-muted-foreground border-l-2 border-primary/50 pl-3"
                   data-testid="regimelab-universe-note">
                {res.universe_note}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {res && (
        <>
          <Microstates micro={res.microstates} />
          <Transitions trans={res.transitions} />
          <Conviction conv={res.conviction} />
        </>
      )}
    </div>
  );
};
